import React, { useState } from "react";
import { Container, Box, CircularProgress, Snackbar, Alert } from "@mui/material";

import CheckHR from "../components/CheckHR";
import CheckHRModal from "../components/CheckHRModal";
import ComparisonHRTable from "../components/ComparisonHRTable";
import Seo from "../components/Seo";
import { trackEvent } from "../utils/analytics";

import API from "../api/api";

function CheckHRPage() {
    const [analysisData, setAnalysisData] = useState(null);
    const [analyzeLoading, setAnalyzeLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);

    const [loading, setLoading] = useState(false);

    const [comparisonData, setComparisonData] = useState([]);
    const [totals, setTotals] = useState(null);
    const [compareLoading, setCompareLoading] = useState(false);

    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState("info");

    const showSnackbar = (msg, severity = "info") => {
        setSnackbarMessage(msg);
        setSnackbarSeverity(severity);
        setSnackbarOpen(true);
    };

    const resetBeforeAnalyze = () => {
        setAnalysisData(null);
        setComparisonData([]);
        setTotals(null);
    };

    const handleCompare = async () => {
        if (!analysisData || !analysisData.files_ready || analysisData.files_ready.length === 0) {
            showSnackbar("Нет файлов для сравнения", "error");
            return;
        }

        if (compareLoading) {
            showSnackbar("Сравнение уже выполняется", "warning");
            return;
        }

        const formData = new FormData();

        const banksMap = {};
        analysisData.files_ready.forEach((f) => {
            if (!f.fileObj || f.bank === "unknown") return;

            if (!banksMap[f.bank]) banksMap[f.bank] = [];
            banksMap[f.bank].push(f.fileObj);
        });

        Object.entries(banksMap).forEach(([bank, files], index) => {
            formData.append(`banks[${index}][bank]`, bank);
            files.forEach((f) => formData.append(`banks[${index}][files]`, f));
        });

        formData.append("excel_file", analysisData.excelFile);

        setCompareLoading(true);
        setComparisonData([]);
        setTotals(null);
        setModalOpen(false);

        trackEvent("compare_hr_started");

        try {
            const response = await API.post("/checker/compare-hr-pp/", formData);
            const data = response.data;

            setComparisonData(data.rows || []);
            setTotals(data.totals || null);

            trackEvent("compare_hr_finished", {
                rows_count: (data.rows || []).length,
            });

            showSnackbar("Сверка завершена", "success");
        } catch (error) {
            console.error(error);

            const message =
                error.response?.data?.error ||
                "Ошибка соединения";

            showSnackbar(message, "error");
        } finally {
            setCompareLoading(false);
        }
    };

    return (
        <>
            <Seo
                title="Сверка платежных поручений с кадровыми данными – Qarzhy"
                description="Сверка банковских платежных поручений с кадровыми данными. Сопоставление ИИН, ФИО и сотрудников, поиск отсутствующих сотрудников и расхождений в написании."
                keywords="сверка платежных поручений с кадровыми данными, сверка сотрудников по ИИН, HR сверка банка, сверка банка и кадров, расхождения ФИО, кадровая сверка выплат"
                url="https://qarzhy.kz/check-hr"
            />

            <Container sx={{ mt: 4 }}>
                <CheckHR
                    setAnalysisData={setAnalysisData}
                    setAnalyzeLoading={setAnalyzeLoading}
                    setModalOpen={setModalOpen}
                    setLoading={setLoading}
                    loading={loading}
                    compareLoading={compareLoading}
                    resetBeforeAnalyze={resetBeforeAnalyze}
                />

                <Box sx={{ mt: 5, display: "flex", justifyContent: "center" }}>
                    {compareLoading ? (
                        <CircularProgress size={60} />
                    ) : (
                        <ComparisonHRTable data={comparisonData} totals={totals} />
                    )}
                </Box>

                <CheckHRModal
                    open={modalOpen}
                    onClose={() => {
                        if (analyzeLoading) return;
                        setModalOpen(false);
                    }}
                    analysisData={analysisData}
                    analyzeLoading={analyzeLoading}
                    onCompare={handleCompare}
                    compareLoading={compareLoading}
                />

                <Snackbar
                    open={snackbarOpen}
                    autoHideDuration={4000}
                    onClose={() => setSnackbarOpen(false)}
                    anchorOrigin={{ vertical: "top", horizontal: "right" }}
                >
                    <Alert severity={snackbarSeverity}>
                        {snackbarMessage}
                    </Alert>
                </Snackbar>
            </Container>
        </>
    );
}

export default CheckHRPage;